// BBALL SINGLES — read hook. Calls get_bball_singles() (security-definer RPC):
// the top soft-vs-sharp +CLV pick per upcoming NBA game. HONEST: real edge only
// (a soft book price beating the de-vigged Pinnacle fair), empty when no outlier
// exists right now. Every pick is status='provisional' until basketball realized
// CLV accrues (the page labels it as such — never presented as proven edge yet).

import { useMemo } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

export interface BballSinglePick {
  fixtureId: string;
  league: string | null;
  home: string;
  away: string;
  kickoffAt: string | null;
  marketGroup: string;   // 'ml' | 'tot:<line>' | 'spr:<line>'
  line: number | null;
  side: string;          // home | away | over | under
  pinOdds: number;
  fairProb: number;      // de-vigged Pinnacle fair probability
  softOdds: number;      // best soft-book price
  bestBook: string;
  edgePct: number;       // (softOdds × fairProb − 1) × 100
  anchor: string;        // 'pinnacle' (sharp) | 'consensus' (market median)
  numBooks: number;      // non-exchange books pricing this side
  isEdge: boolean;       // genuine soft-beats-sharp +CLV (true Pinnacle anchor only)
  tier: string;          // 'edge' | 'sharp_read'
  status: string;        // 'provisional' until CLV-proven
}

export interface BballSinglesState {
  loading: boolean;
  error: string | null;
  picks: readonly BballSinglePick[];
  refetch: () => void;
}

function marketLabel(group: string, line: number | null, side: string): string {
  const s = side.charAt(0).toUpperCase() + side.slice(1);
  if (group === "ml") return `Moneyline · ${s}`;
  if (group.startsWith("tot:")) return `Total ${line ?? ""} · ${s}`;
  if (group.startsWith("spr:")) return `Spread ${line ?? ""} · ${s}`;
  return `${group} · ${s}`;
}

function shape(r: Record<string, unknown>): BballSinglePick {
  const line = typeof r.line === "number" ? r.line : (r.line != null ? Number(r.line) : null);
  return {
    fixtureId: String(r.fixture_id ?? ""),
    league: typeof r.league === "string" ? r.league : null,
    home: String(r.home_team ?? ""),
    away: String(r.away_team ?? ""),
    kickoffAt: typeof r.kickoff_at === "string" ? r.kickoff_at : null,
    marketGroup: String(r.market_group ?? ""),
    line,
    side: String(r.side ?? ""),
    pinOdds: Number(r.pin_odds ?? 0),
    fairProb: Number(r.fair_prob ?? 0),
    softOdds: Number(r.soft_odds ?? 0),
    bestBook: String(r.best_book ?? ""),
    edgePct: Number(r.edge_pct ?? 0),
    anchor: String(r.anchor ?? "consensus"),
    numBooks: Number(r.num_books ?? 0),
    isEdge: r.is_edge === true,
    tier: String(r.tier ?? "sharp_read"),
    status: String(r.status ?? "provisional"),
  };
}

/** Human market label for a pick (exported for the screen). */
export function bballSingleMarketLabel(p: BballSinglePick): string {
  return marketLabel(p.marketGroup, p.line, p.side);
}

/** Who the pick backs, in plain language (the side the odds favour). */
export function bballPickTarget(p: BballSinglePick): string {
  const ln = p.line ?? 0;
  if (p.marketGroup === "ml") return `${p.side === "home" ? p.home : p.away} to win`;
  if (p.marketGroup.startsWith("spr:")) {
    return p.side === "home" ? `${p.home} −${ln}` : `${p.away} +${ln}`;
  }
  if (p.marketGroup.startsWith("tot:")) {
    return `${p.side === "over" ? "Over" : "Under"} ${ln} total points`;
  }
  return `${p.side}`;
}

/** Fair (de-vigged) decimal odds implied by the read. */
export function bballFairOdds(p: BballSinglePick): number {
  return p.fairProb > 0 ? 1 / p.fairProb : 0;
}

/** Honest, derived rationale — no fabricated narrative, no formula leak. */
export function bballPickReason(p: BballSinglePick): string {
  const src =
    p.anchor === "pinnacle"
      ? "Priced off the sharp (Pinnacle) line"
      : `Priced off the market consensus across ${p.numBooks} book${p.numBooks !== 1 ? "s" : ""}`;
  const chance = `the read gives it roughly a ${Math.round(p.fairProb * 100)}% chance`;
  if (p.isEdge) {
    return `${src}; ${chance}. ${p.bestBook} is offering ${p.softOdds.toFixed(2)} — above the fair line, so this is genuine value (+${p.edgePct.toFixed(1)}% vs fair).`;
  }
  return `${src}; ${chance}. Best available is ${p.softOdds.toFixed(2)} at ${p.bestBook}, around fair odds. It still carries the book's normal margin — treat it as a confident lean, not a value bet.`;
}

export function useBballSingles(): BballSinglesState {
  // Cheap RLS-gated read; refresh on return/focus + light 60s poll (live odds).
  const q = useRpcQuery<Record<string, unknown>[] | null>("get_bball_singles", { refetchInterval: 60_000 });
  const picks = useMemo<readonly BballSinglePick[]>(
    () => (Array.isArray(q.data) ? q.data.map(shape) : []),
    [q.data],
  );
  return useMemo(
    () => ({ loading: q.isLoading, error: q.error ? q.error.message : null, picks, refetch: () => { void q.refetch(); } }),
    [q.isLoading, q.error, picks, q.refetch],
  );
}
